import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin, Phone } from "lucide-react";
import { api } from "../lib/api";
import { CONTACT, IMG, SHOP_CATEGORIES, TEAM } from "../lib/content";
import { FadeUp, MaskedLines, Chapter, GoldLink, GhostLink } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import Triskelion from "../components/Triskelion";
import DiscountSection from "../components/DiscountSection";
import PromoRibbon from "../components/PromoRibbon";
import { toast } from "sonner";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src="/hero-three-legs.jpg" alt="Celtic Gold Three Legs of Man pendant with Peel Castle beyond" className="w-full h-full object-cover object-[50%_62%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <div className="absolute inset-x-0 top-1/3 h-px overflow-hidden pointer-events-none">
        <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-gold to-transparent animate-gold-sweep" />
      </div>

      <Triskelion className="absolute right-[8%] top-[18%] w-40 h-40 lg:w-64 lg:h-64 text-gold/15 animate-spin-slow hidden sm:block" strokeWidth={6} />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 pt-48 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-14 bg-gold" />
          <span className="text-xs uppercase tracking-[0.35em] text-gold font-medium">Celtic Gold Ltd · Est. 1982 · Peel</span>
        </motion.div>

        <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl text-ivory font-light leading-[1.02] tracking-tight max-w-4xl" data-testid="hero-headline">
          <MaskedLines lines={["Jewellery Inspired", "by the Isle of Man"]} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 text-stone-mist text-base sm:text-lg font-light max-w-xl leading-relaxed"
        >
          Manx, Celtic and Archibald Knox-inspired jewellery, handcrafted with care in our workshop in Peel.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <GoldLink to="/shop/all" testId="hero-explore">Explore the Collection</GoldLink>
          <GhostLink to="/bespoke" testId="hero-bespoke">Create Something Bespoke</GhostLink>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-stone-mist"
      >
        <span className="text-[10px] uppercase tracking-[0.35em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce text-gold" />
      </motion.div>
    </section>
  );
}

function Categories() {
  return (
    <section className="py-20 lg:py-28 relative grain" data-testid="section-categories">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="01" eyebrow="The Shop" title="Eight ways to discover Celtic Gold" className="mb-14" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SHOP_CATEGORIES.map((cat, i) => (
            <FadeUp key={cat.slug} delay={i * 0.06} className={i === 0 || i === 6 ? "lg:col-span-2" : ""}>
              <Link
                to={cat.href || `/shop/${cat.slug}`}
                data-testid={`home-category-${cat.slug}`}
                className={`group relative block overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700 ${
                  i === 0 || i === 6 ? "h-72 sm:h-80" : "h-56 sm:h-80"
                }`}
              >
                <img src={cat.image} alt={cat.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <h3 className={`font-serif text-ivory group-hover:text-gold transition-colors duration-500 ${i === 0 || i === 6 ? "text-3xl" : "text-xl"}`}>{cat.name}</h3>
                  <p className="text-stone-mist text-xs mt-2 max-w-[240px]">{cat.tagline}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Heritage() {
  return (
    <section className="py-20 lg:py-32 bg-ink-2/50" data-testid="section-heritage">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <FadeUp>
          <div className="relative">
            <img src={IMG.peel} alt="Peel Castle, Isle of Man" className="w-full aspect-[4/5] object-cover border border-gold/20" />
            <div className="absolute -bottom-6 -right-6 bg-ink border border-gold/30 px-8 py-6 hidden sm:block">
              <p className="font-mono text-gold text-3xl">1982</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-stone-mist mt-1">Established</p>
            </div>
          </div>
        </FadeUp>
        <div>
          <Chapter number="02" eyebrow="Our Heritage" title="A Lifelong Passion for Jewellery" className="mb-8" />
          <FadeUp delay={0.15}>
            <p className="text-stone-mist leading-relaxed font-light mb-5">
              Celtic Gold is a family-run jewellery business based in Peel on the west coast of the Isle of Man,
              established in 1982. We combine a welcoming retail shop with an experienced, fully equipped jewellery
              workshop.
            </p>
            <p className="text-stone-mist leading-relaxed font-light mb-5">
              Our designs draw inspiration from the Celts and Vikings who settled on the Island, from traditional
              Manx motifs, and from the work of renowned Manx artist and designer Archibald Knox — a living
              connection between the Island's history and the jewellery we create today.
            </p>
            <div className="flex gap-10 my-8 border-y border-gold/15 py-6">
              {[["1982", "Established"], ["3rd", "Generation"], ["1", "Island workshop"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-serif text-3xl text-gold">{v}</p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-stone-mist mt-1">{l}</p>
                </div>
              ))}
            </div>
            <GhostLink to="/about" testId="heritage-story">Discover Our Story</GhostLink>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then(({ data }) => setItems(data.items)).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section className="py-20 lg:py-28" data-testid="section-featured">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <Chapter number="03" eyebrow="Handpicked" title="Featured Jewellery" />
          <Link to="/shop/all" className="hidden sm:flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow transition-colors" data-testid="featured-view-all">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {items.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Collections() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/collections").then(({ data }) => setItems(data)).catch(() => {});
  }, []);
  return (
    <section className="py-20 lg:py-28 bg-ink-2/50" data-testid="section-collections">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="04" eyebrow="Celtic & Manx Heritage" title="Collections shaped by the Island" className="mb-14" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.slice(0, 6).map((col, i) => (
            <FadeUp key={col.slug} delay={i * 0.07}>
              <Link
                to={`/collections/${col.slug}`}
                data-testid={`home-collection-${col.slug}`}
                className="group relative block h-72 overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700"
              >
                <img src={col.image} alt={col.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-serif text-2xl text-ivory group-hover:text-gold transition-colors duration-500">{col.name}</h3>
                  <p className="text-stone-mist text-xs mt-2 line-clamp-2 max-w-xs">{col.description}</p>
                  <span className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.25em] mt-4">
                    Explore Collection <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Bespoke() {
  return (
    <section className="relative py-28 lg:py-40 overflow-hidden" data-testid="section-bespoke">
      <div className="absolute inset-0">
        <img src={IMG.carving} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/80 to-ink" />
      </div>
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <FadeUp>
          <Triskelion className="w-12 h-12 text-gold mx-auto mb-8" strokeWidth={8} />
          <p className="text-xs uppercase tracking-[0.35em] text-gold mb-6">05 · Bespoke Jewellery</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory font-light tracking-tight">Made for You</h2>
          <p className="mt-8 text-stone-mist leading-relaxed font-light max-w-2xl mx-auto">
            Our fully equipped workshop allows you to discuss unique jewellery ideas and commission bespoke pieces.
            We work closely with you throughout the process — keeping you updated at each stage — with the majority
            of the craftsmanship completed in-house on the Isle of Man.
          </p>
          <p className="mt-4 text-stone-mist/70 text-sm font-light">Bespoke commissions depend on jeweller availability and the design required.</p>
          <div className="mt-10">
            <GoldLink to="/bespoke" testId="bespoke-start">Start a Bespoke Enquiry</GoldLink>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Workshop() {
  const steps = ["Idea", "Design", "Craftsmanship", "Hand Finishing", "Finished Piece"];
  return (
    <section className="py-20 lg:py-32" data-testid="section-workshop">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="06" eyebrow="The Workshop" title="Crafted in Our Isle of Man Workshop" className="mb-14" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.workshop} alt="Jeweller at work" className="w-full aspect-square object-cover border border-white/5" />
              <img src={IMG.setting} alt="Stone setting" className="w-full aspect-square object-cover border border-white/5 mt-8" />
            </div>
          </FadeUp>
          <div>
            <FadeUp>
              <p className="text-stone-mist leading-relaxed font-light mb-10">
                Our collections are carefully created using the lost wax casting process, followed by meticulous hand
                finishing. The workshop is managed by skilled jewellers who can assist with repairs, remodelling,
                bespoke commissions, cleaning, polishing, engraving and restoration work.
              </p>
            </FadeUp>
            <div className="space-y-0">
              {steps.map((step, i) => (
                <FadeUp key={step} delay={i * 0.08}>
                  <div className="flex items-center gap-5 py-4 border-b border-white/5 group">
                    <span className="font-mono text-xs text-gold w-8">0{i + 1}</span>
                    <span className="font-serif text-xl text-ivory group-hover:text-gold transition-colors duration-300">{step}</span>
                    <span className="ml-auto h-px flex-1 max-w-[80px] bg-gold/20 group-hover:bg-gold/60 transition-colors duration-500" />
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.4} className="mt-10">
              <GhostLink to="/about#jewellers" testId="workshop-meet">Meet the Hands Behind the Jewellery</GhostLink>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

function Team() {
  return (
    <section className="py-20 lg:py-28 bg-ink-2/50" data-testid="section-team">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Chapter number="07" eyebrow="The People" title="Meet the Celtic Gold Team" className="mb-6" />
        <FadeUp>
          <p className="text-stone-mist font-light mb-12 max-w-xl">A small, friendly team with generations of jewellery knowledge and experience.</p>
        </FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-5">
          {TEAM.map((member, i) => (
            <FadeUp key={member.name + member.role} delay={i * 0.06}>
              <div className="border border-white/5 hover:border-gold/40 transition-colors duration-500 p-6 text-center h-full" data-testid={`team-card-${i}`}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border border-gold/40 flex items-center justify-center">
                  <span className="font-serif text-2xl text-gold">{member.name.length <= 2 ? member.name : member.name[0]}</span>
                </div>
                <p className="text-ivory font-light">{member.name}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-stone-mist mt-1">{member.role}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function News() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/news", { params: { limit: 3 } }).then(({ data }) => setItems(data.items)).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section className="py-20 lg:py-28" data-testid="section-news">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <Chapter number="08" eyebrow="Journal" title="Latest News" />
          <Link to="/news" className="hidden sm:flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow transition-colors" data-testid="news-view-all">
            View All News <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((art, i) => (
            <FadeUp key={art.slug} delay={i * 0.08}>
              <Link to={`/news/${art.slug}`} className="group block" data-testid={`news-card-${art.slug}`}>
                <div className="overflow-hidden border border-white/5 group-hover:border-gold/40 transition-colors duration-700 mb-5">
                  <img src={art.image} alt={art.title} loading="lazy" className="w-full aspect-[16/10] object-cover transition-transform duration-[1.2s] group-hover:scale-108 group-hover:scale-110" />
                </div>
                <p className="font-mono text-xs text-gold mb-2">{new Date(art.published).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                <h3 className="font-serif text-2xl text-ivory group-hover:text-gold transition-colors duration-300">{art.title}</h3>
                <p className="text-stone-mist text-sm font-light mt-3 line-clamp-2">{art.excerpt}</p>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Visit() {
  return (
    <section className="py-20 lg:py-28 bg-ink-2/50" data-testid="section-visit">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-stretch">
        <div className="flex flex-col justify-center">
          <Chapter number="09" eyebrow="Visit Us in Peel" title="Our shop on Michael Street" className="mb-8" />
          <FadeUp delay={0.1}>
            <p className="text-stone-mist leading-relaxed font-light mb-6">
              Our welcoming shop provides a relaxed environment where you can browse jewellery and gifts at your
              leisure. Our friendly team are available to provide assistance, advice and personal service — and the
              online shop represents only a selection of what you will find in store.
            </p>
            <div className="space-y-3 mb-8">
              <p className="flex items-start gap-3 text-ivory/90 text-sm">
                <MapPin className="w-4 h-4 text-gold mt-0.5 shrink-0" /> 5 Michael Street, Peel, Isle of Man, IM5 1HA
              </p>
              <a href={`tel:${CONTACT.phoneShort.replace(/\s/g, "")}`} className="flex items-center gap-3 text-ivory/90 text-sm hover:text-gold transition-colors" data-testid="visit-phone">
                <Phone className="w-4 h-4 text-gold shrink-0" /> {CONTACT.phone}
              </a>
            </div>
            <GhostLink to="/contact" testId="plan-visit">Plan Your Visit</GhostLink>
          </FadeUp>
        </div>
        <FadeUp delay={0.15}>
          <div className="border border-gold/20 h-full min-h-[360px]">
            <iframe
              title="Celtic Gold — 5 Michael Street, Peel, Isle of Man"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-4.7200%2C54.2200%2C-4.6900%2C54.2320&layer=mapnik&marker=54.2257%2C-4.6985"
              className="w-full h-full min-h-[360px] grayscale invert-[0.9] contrast-[0.9]"
              loading="lazy"
              data-testid="visit-map"
            />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/enquiries", { type: "newsletter", email, message: "Newsletter signup" });
      setSent(true);
      toast.success("Thank you — you're on the list.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };
  return (
    <section className="py-20 lg:py-28 border-t border-gold/10" data-testid="section-newsletter">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
        <FadeUp>
          <Triskelion className="w-10 h-10 text-gold mx-auto mb-6" strokeWidth={8} />
          <h2 className="font-serif text-3xl sm:text-4xl text-ivory font-light">News from the workshop</h2>
          <p className="text-stone-mist font-light mt-4 mb-8">New pieces, collections and stories from Peel — straight to your inbox.</p>
          {sent ? (
            <p className="text-gold font-mono text-sm tracking-wider" data-testid="newsletter-success">Thank you for subscribing.</p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="newsletter-email"
                className="flex-1 bg-ink-2 border border-white/10 focus:border-gold outline-none px-5 py-4 text-sm text-ivory placeholder:text-stone-mist/60 transition-colors"
              />
              <button type="submit" data-testid="newsletter-submit" className="bg-gold text-ink px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors duration-300">
                Subscribe
              </button>
            </form>
          )}
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <PromoRibbon />
      <Marquee />
      <Categories />
      <Heritage />
      <FeaturedProducts />
      <Collections />
      <Bespoke />
      <Workshop />
      <Team />
      <News />
      <Visit />
      <DiscountSection />
      <Newsletter />
    </main>
  );
}
